import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FiBell } from "react-icons/fi";
import { Dialog, DialogContent } from "@mui/material";
import moment from "moment";
import { useLocation, useNavigate } from "react-router";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";

import styles from "./VideoKyc.module.scss";
import { handlehideContextualLoader, handleShowContextualLoader } from "../../utils/contextualLoaderTrigger";
import { setVKYCStep } from "../../redux/slices/userSlice";

export default function VKYCInReview() {
  const [data, setData] = useState(null);
  const [showNotifyPopup, setShowNotifyPopup] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { transaction_id, vkyc_status, api_response } = location.state || {};

  const transactionID = transaction_id || localStorage.getItem("transaction_id");

  const vkycStatus = vkyc_status || localStorage.getItem("vkyc_status");

  useEffect(() => {
    dispatch(setVKYCStep("VKYC_IN_REVIEW"));

    if (!transactionID || !vkycStatus) {
      return;
    }

    let cancelled = false;
    let timeoutId;

    const fetchStatusDetails = async () => {
      try {
        const { vkycStatusDetailsAPI } = new AfterOnboardAPI();

        const res = await vkycStatusDetailsAPI(transactionID, vkycStatus, api_response);

        if (cancelled) return;

        const result = res?.data;

        if (!result) {
          return;
        }

        setData(result);

        // Rejected
        if (result.leadStatus === "Rejected Process") {
          navigate("/video-kyc-rejected", {
            state: {
              transaction_id: transactionID,
              vkyc_status: vkycStatus,
              api_response,
            },
            replace: true,
          });

          return;
        }

        // Approved
        if (result.leadStatus === "Approved" || result.leadStatus === "Disbursed" || result.leadStatus === "Disbursal Sheet Send") {
          navigate("/video-kyc-approved", {
            replace: true,
          });

          return;
        }

        if (result.leadStatus === "Closed") {
          navigate("/home", {
            replace: true,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("VKYC review error:", error);
        }
      } finally {
        // Schedule the next request after the current one finishes
        if (!cancelled) {
          timeoutId = setTimeout(fetchStatusDetails, 30 * 1000);
        }

        handlehideContextualLoader();
      }
    };

    fetchStatusDetails();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [transactionID, vkycStatus, api_response, dispatch, navigate]);

  // Open notification modal
  const handleNotifyMe = () => {
    setShowNotifyPopup(true);
  };

  // Close modal and logout
  const handleClosePopup = () => {
    dispatch(setVKYCStep(""));
    setShowNotifyPopup(false);
    window.location.replace("/logout");
  };

  return (
    <div className="wrapper">
      <main className={styles.page}>
        {/* Main Content */}
        <section className={styles.content}>
          {/* Loading Spinner */}
          <div className={`${styles.statusIcon} ${styles.statusIconPending}`}>
            <span className={styles.spinner} role="status" aria-label="Loading" />
          </div>

          {/* Status Badge */}
          <div className={`${styles.statusPill} ${styles.statusPillPending}`}>IN REVIEW</div>

          {/* Heading */}
          <h1 className={styles.title}>Video KYC in review</h1>

          {/* Description */}
          <p className={styles.description}>An auditor is reviewing your session. This usually takes 5–10 minutes. We&apos;ll notify you once complete.</p>

          {/* Details Card */}
          <div className={styles.detailsCard}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Session ID</span>

              <strong className={styles.detailValue}>{data?.vkyc_transaction_id || "-"}</strong>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Timestamp</span>

              <strong className={styles.detailValue}>{data?.vkyc_initiate_date ? moment(data.vkyc_initiate_date).format("DD-MM-YYYY HH:mm:ss") : "-"}</strong>
            </div>

            <div className={`${styles.detailRow} ${styles.detailRowLast}`}>
              <span className={styles.detailLabel}>Loan amount</span>

              <strong className={styles.loanAmount}>₹{data?.approved_loan_amount || 0}</strong>
            </div>
          </div>
        </section>

        {/* Fixed Bottom Button */}
        <div className={styles.footer}>
          <button type="button" className={styles.actionButton} onClick={handleNotifyMe}>
            Notify Me
          </button>
        </div>

        {/* Material UI Notification Modal */}
        <Dialog
          open={showNotifyPopup}
          onClose={(_, reason) => {
            // Prevent closing by clicking outside or pressing Escape
            // if (reason === "backdropClick" || reason === "escapeKeyDown") {
            //   return;
            // }

            handleClosePopup();
          }}
          disableEscapeKeyDown
          maxWidth="xs"
          fullWidth
          aria-labelledby="vkyc-notify-title"
          aria-describedby="vkyc-notify-description"
          slotProps={{
            paper: {
              className: styles.notifyModalPaper,
              elevation: 0,
            },
          }}
        >
          <DialogContent className={styles.modalContent}>
            {/* Notification Icon */}
            <div className={styles.modalIcon}>
              <FiBell size={25} />
            </div>

            {/* Modal Heading */}
            <h2 id="vkyc-notify-title" className={styles.modalTitle}>
              We will notify you
            </h2>

            {/* Modal Description */}
            <p id="vkyc-notify-description" className={styles.modalDescription}>
              We will notify you once we have any update on your Video KYC verification.
            </p>

            {/* Modal Button */}
            <button type="button" className={styles.modalButton} onClick={handleClosePopup}>
              Okay, Got it
            </button>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
